import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';

/**
 * Hook for bulk importing images to a project
 * Leverages the existing project assignment patterns from the Add to Project system
 */
export const useBulkImageImport = (targetProjectId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (imageIds: string[]) => {
      if (imageIds.length === 0) {
        throw new Error('No images selected for import');
      }

      console.log('[BulkImageImport] Importing images to project:', {
        targetProjectId,
        imageCount: imageIds.length,
        imageIds
      });

      // Update all selected images to assign them to the target project
      const { data, error } = await supabase
        .from('workbook_images')
        .update({ workbook_id: targetProjectId })
        .in('id', imageIds)
        .select('id, filename, workbook_id');

      if (error) {
        console.error('[BulkImageImport] Error updating images:', error);
        throw error;
      }

      console.log('[BulkImageImport] Successfully imported images:', data);
      return data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['user-images-with-projects'] });
      queryClient.invalidateQueries({ queryKey: ['project-images', targetProjectId] });
      queryClient.invalidateQueries({ queryKey: ['private-image-library'] });
      
      // CRITICAL: Invalidate the infinite user images sidebar cache
      queryClient.invalidateQueries({ queryKey: ['infinite-user-images'] });
      
      // Show success toast
      toast({
        title: "Images Imported Successfully",
        description: `${data.length} image${data.length === 1 ? '' : 's'} imported to project.`,
        variant: "success",
      });
    },
    onError: (error: Error) => {
      console.error('[BulkImageImport] Import failed:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import images. Please try again.",
        variant: "destructive",
      });
    },
  });
}; 